import { ActionsFrame, PageActions } from "../../../../shared/ui/PageActions";
import { StaleBuildNotice } from "./StaleBuildNotice";
import { useTranslation } from "../i18n/useTranslation";
import { WebHeader } from "../../../../shared/ui/WebHeader";
import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import Footer from "./Footer";

/**
 * WEB V2'S PAGE, for the book.* screens it draws as pages rather than as steps
 * of the booking flow: the public-link refusals, the paused professional, the
 * secure appointment link, not-found. Source: Lura/designs/lura-web-v2/w2-kit.jsx
 * (W2Shell) and w2-links.jsx (w2-links.jsx has changed there since; the copy
 * this was ported from is in git at 17b3da5e:docs/design/lura-web-v2/).
 *
 * Wordmark, one 400px column, at most one action at the foot of it, and the
 * legal line. No top bar and no card: "Content sits on paper, exactly as it
 * does in the app. What makes it look deliberate is a measured column, real
 * type and one full-width action."
 *
 * THE ONE <main> FOR book.*'s PAGES (c3 folded AppointmentAction's and the
 * profile's local shells in; the booking-flow steps are c6). Three layouts,
 * each the exact markup its screens had, so nothing moves on screen:
 *
 * - `page` (default): the v3 page. Header, a scrolling column (<main>) with
 *   the legal footer as its sibling, and the action bar pinned below, fed
 *   by `foot` or any PageActions inside (LoadFailed's Try again).
 * - `management`: the secure appointment link (v3 "management" page). A wider
 *   column, and the pinned action bar INSIDE <main>: it holds the page's task
 *   (Cancel, Keep, Get help), and outside every landmark a link there was
 *   content landmark navigation could not reach (axe `region`). `testId` and
 *   `state` go on the column, which is what every test reads.
 * - `flow`: a page drawn in the booking step's shape (the account-gated
 *   profile): the step header inside <main>, a scroll area holding the legal
 *   line, and `foot` rendered AS GIVEN after it (the profile's `.b-foot`,
 *   which the desktop layer places in the right-hand rail; a grid can only
 *   place a grid item).
 *
 * WHERE A SCREEN'S STATES SHARE A LAYOUT, it renders that one PageShell for
 * all of them, so the header and the legal line stay mounted as the state
 * changes and a control focused in them keeps focus: the secure appointment
 * link draws `management` while it loads, fails and settles. That is not true
 * of every screen. The profile draws its refusals and its load failure on
 * `page` and its account-gated ready state on `flow` (and its loading state
 * is the booking step's skeleton, not a PageShell), so its frame remounts
 * when it moves between them, after a Try again for example.
 *
 * `testId` and `state` are carried onto the <main> (the column, for
 * `management`) because every caller already exposes its state to the suite
 * that way.
 */
export function PageShell({
  children,
  foot,
  testId,
  state,
  legal = true,
  meta,
  layout = "page",
}: {
  children: ReactNode;
  foot?: ReactNode;
  testId?: string;
  state?: string;
  /** RouteError renders without the footer: it may be showing because the
   *  footer's own dependencies failed. */
  legal?: boolean;
  /** A page web v2 draws with `legal={false}` that still owes the reader the
   *  language switch and cookie settings (StepMeta). Rendered only without
   *  the legal footer, in its own contentinfo landmark. */
  meta?: ReactNode;
  layout?: "page" | "management" | "flow";
}) {
  const { locale, t } = useTranslation();
  if (layout === "management") {
    return (
      <div className="b2-page b3-management">
        <WebHeader locale={locale} className="" markClassName="b2-mark" />
        <main className="b3-management-main">
          <ActionsFrame className="b3-management-foot">
            <div className="b3-management-scroll">
              <div className="b2-col" data-testid={testId} data-state={state}><StaleBuildNotice />{children}</div>
              {legal ? <Footer /> : null}
            </div>
            {foot ? <PageActions className="b2-foot">{foot}</PageActions> : null}
          </ActionsFrame>
        </main>
      </div>
    );
  }
  if (layout === "flow") {
    return (
      <main className="b-app b2-flow b3-flow" data-testid={testId} data-state={state}>
        <header className="b2-stephead">
          <WebHeader as="div" locale={locale} />
        </header>
        <div className="b-scroll"><StaleBuildNotice />{children}{legal ? <Footer /> : null}</div>
        {foot}
      </main>
    );
  }
  // THREE LANDMARKS, as on the booking flow's shells: the wordmark in a
  // banner, the page in <main>, the legal line in its own contentinfo. The
  // footer was first placed inside <main>, which cost it the contentinfo role
  // on /a and not-found, where it had been a sibling of <main>. Raised in
  // review. `.b2-page` is the scroll container that holds all three.
  return (
    <div className="b2-page b3-page">
      {/* Labelled, so the action bar is a region landmark as on app.*
          (AppShell): everything in it, the reCAPTCHA line above the action
          included, sits in a landmark (axe region). */}
      <ActionsFrame className="b3-page-actions" label={t("page.actions")}>
      <WebHeader locale={locale} className="" markClassName="b2-mark" />
      <div className="b3-page-scroll">
      <main className="b2-col" data-testid={testId} data-state={state}>
        <StaleBuildNotice />
        {children}
        {foot ? <PageActions className="b2-foot">{foot}</PageActions> : null}
      </main>
      {legal ? <Footer /> : meta ? <footer className="b2-meta">{meta}</footer> : null}
      </div>
      </ActionsFrame>
    </div>
  );
}

/** One heading per page, and it does not change as the page resolves. */
export function PageHead({
  title,
  sub,
  headingRef,
  subRole,
}: {
  title: ReactNode;
  sub?: ReactNode;
  headingRef?: Ref<HTMLHeadingElement>;
  /**
   * `alert` makes only the sub line the live region. With focus on the
   * heading, each part is then spoken once: the heading on focus, the line as
   * the alert (W-2).
   *
   * When there is no sub line (the v3 copy pass emptied the failed-read
   * bodies), the heading's own container is the alert instead, so the state
   * is still announced as it appears, with the existing title and no new
   * copy. Exactly one live region either way.
   */
  subRole?: "alert";
}) {
  const headAlerts = subRole === "alert" && !sub;
  return (
    <div className="b2-head" role={headAlerts ? "alert" : undefined}>
      <h1
        className="b2-h1"
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
      >
        {title}
      </h1>
      {sub ? <p className="b2-sub" role={subRole}>{sub}</p> : null}
    </div>
  );
}

/*
 * THE BOOKING FLOW'S ONE SHELL (c6). `/:slug/book/:step` is one route
 * component (views/Book/Book.tsx) that returns a skeleton, a step, or an
 * outcome. When each drew its own <main> and header, every transition
 * unmounted the header, and a keyboard user on Appearance (or the language
 * switch) was dropped to <body> (rc.1 known issue; App fixed the same in c2).
 *
 * Book.tsx renders ONE FlowShell, at one tree position, around every flow
 * branch; React keeps its header (WebHeader) node across them. Each branch
 * still draws its own frame through FlowFrame, which, inside the shell,
 * publishes the frame's chrome (the lead under the wordmark, the <main>'s
 * class, test id and data attributes) to the shell and renders only its body.
 * Outside a shell (unit tests, a page rendered alone) FlowFrame draws the
 * whole frame itself, the same markup the step drew before c6. The shell
 * keeps the header's NODE, not focus: a step that moves focus on arrival
 * still does.
 *
 * c6-2: THE GUEST CHECKOUT AND THE OUTCOMES JOIN IT.
 *  - The chrome lives in FlowShellProvider, which Book places above BOTH the
 *    guest checkout's host (views/Book/guest/GuestFlowHost.tsx) and the shell.
 *    The host stays where it is, so its checkout, identity claim and Terms
 *    state survive step changes, and renders its screens INTO the shell's
 *    <main> through FlowShellPortal; their frames reach the provider through
 *    React's tree, not the DOM's. Where no shell is mounted, the portal
 *    renders nothing, never a <main> of its own.
 *  - The outcome is a frame VARIANT. Its page put the wordmark beside its
 *    <main>; React cannot keep a node that moves between parents, so the
 *    outcome now uses the shell's <main> (classes `b2-outcome-page
 *    b3-outcome-page b2-outcome`) and the shell's header becomes
 *    `display: contents`, leaving the same WebHeader laid out as the page's
 *    own flex child, exactly where it was (v3.css, "c6-2 outcome").
 *
 * Published in a layout effect, so the shell re-renders with the new chrome
 * before the browser paints: no frame without a lead or a test id.
 */

/** What a flow frame contributes to the shell. */
export type FlowChrome = {
  testId?: string;
  /** Extra classes on <main>, beside the variant's own. */
  className?: string;
  /** `data-*` attributes on <main> (`{ hold: "held" }` → `data-hold="held"`). */
  data?: Record<string, string | undefined>;
  /** Under the wordmark, inside the step header: Back and the step count. */
  lead: ReactNode;
  /** A step (the default) or the terminal outcome page. */
  variant?: "step" | "outcome";
};

type FlowShellContextValue = {
  publish: (chrome: FlowChrome | null) => void;
  chrome: FlowChrome | null;
  /** The mounted shell's <main>, for FlowShellPortal; null when none is. */
  element: HTMLElement | null;
  setElement: (element: HTMLElement | null) => void;
};

const FlowShellContext = createContext<FlowShellContextValue | null>(null);

function dataAttributes(data: FlowChrome["data"]): Record<string, string> {
  const attributes: Record<string, string> = {};
  for (const [key, value] of Object.entries(data ?? {})) {
    if (value !== undefined) attributes[`data-${key}`] = value;
  }
  return attributes;
}

function mainClass(chrome: Pick<FlowChrome, "className" | "variant"> | null): string {
  const base = chrome?.variant === "outcome" ? "b-app b2-outcome-page b3-outcome-page b2-outcome" : "b-app b2-flow b3-flow";
  return chrome?.className ? `${base} ${chrome.className}` : base;
}

/** The frame's <main>, header and body: one markup for the shell and for a frame drawn alone. */
function FrameMarkup({
  chrome,
  mainRef,
  children,
}: {
  chrome: FlowChrome | null;
  mainRef?: Ref<HTMLElement>;
  children: ReactNode;
}) {
  const { locale } = useTranslation();
  const outcome = chrome?.variant === "outcome";
  /*
   * THE STALE-BUILD NOTICE GOES INTO THE STEP'S OWN SCROLL AREA (review of
   * d8f834c, P1): there it has the step's gutter and, on desktop, its grid
   * cell. A direct child of this <main> sat outside both. `.b3-stale` puts
   * it first (order -1). A screen with no scroll area gets it inline.
   */
  const main = useRef<HTMLElement | null>(null);
  const [scrollArea, setScrollArea] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const found = main.current?.querySelector<HTMLElement>(":scope > .b-scroll") ?? null;
    if (found !== scrollArea) setScrollArea(found);
  });
  // A STABLE callback ref (IF-3 commit guard, rc.9; a measured reason for
  // manual memoization). A new function on every render makes React detach
  // the old ref and attach the new one on every commit: mainRef(null), then
  // mainRef(element). The shell's mainRef is its state setter, so each
  // render queued state updates and re-rendered: +2 commits on every day
  // tap (bisect to 14a272f0, qe-evidence/2026-10-15-rc9-ab-if3).
  const setMain = useCallback(
    (element: HTMLElement | null) => {
      main.current = element;
      if (typeof mainRef === "function") mainRef(element);
      else if (mainRef) (mainRef as { current: HTMLElement | null }).current = element;
    },
    [mainRef]
  );
  return (
    <main ref={setMain} className={mainClass(chrome)} data-testid={chrome?.testId} {...dataAttributes(chrome?.data)}>
      <header className={outcome ? "b3-outcome-head" : "b2-stephead"}>
        {/* Always a div: a different element type here would remount the
            very node the shell exists to keep. */}
        <WebHeader
          as="div"
          locale={locale}
          className={outcome ? "b2-outcome-header" : ""}
          markClassName={outcome ? "b2-mark" : "b2-stepmark"}
        />
        {outcome ? null : chrome?.lead}
      </header>
      {/* The flow's steps draw through this frame, not PageShell's layouts:
          the stale-build notice belongs here too (rc.9 item 2). */}
      {scrollArea ? createPortal(<StaleBuildNotice />, scrollArea) : <StaleBuildNotice />}
      {children}
    </main>
  );
}

/**
 * Where the flow's chrome lives: above the shell AND the guest checkout's
 * host, so a frame rendered into the shell through FlowShellPortal still
 * publishes to it. Book renders it once, at one tree position.
 */
export function FlowShellProvider({ children }: { children: ReactNode }) {
  const [chrome, publish] = useState<FlowChrome | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);
  return (
    <FlowShellContext.Provider value={{ publish, chrome, element, setElement }}>
      {children}
    </FlowShellContext.Provider>
  );
}

/*
 * FOCUS ON A USER'S STEP ADVANCE (c6-3, Web Director's ruling 2026-10-07).
 *
 * When a keyboard or screen-reader user pressed Continue, the control they
 * were on unmounted with its step, and focus fell to <body>: they were sent
 * back to the top of the document at every step. Now the action that moves
 * the step asks for it (`requestStepFocus`, from Continue and Back), and the
 * shell focuses the NEW step's h1 (tabindex -1) when that step arrives, so
 * its heading is what is read next. A ring shows only for keyboard focus
 * (index.css `:focus-visible`).
 *
 * Only a request moves focus. A deep link, a reload, a redirect or an
 * identity change arrives without one, so focus is left alone there, and
 * StepDetails' rule (an ordinary visit never seizes focus or raises a phone
 * keyboard) holds; an h1 raises no keyboard anyway. A request is honoured
 * once, by the next screen with a focusable heading (a step's h1 carries
 * tabindex -1; a skeleton's hidden heading does not), and only within a few
 * seconds: a Continue that went nowhere cannot take focus on some later,
 * unrelated arrival. A step that moves focus itself on arrival (its own
 * heading, Review's uncertain notice, a rejected field) keeps that focus.
 */
const STEP_FOCUS_WINDOW_MS = 3_000;
let stepFocusRequestedAt: number | null = null;

/** The user moved the flow to another step: focus that step's heading when it arrives. */
export function requestStepFocus(now: number = Date.now()): void {
  stepFocusRequestedAt = now;
}

function stepFocusPending(now: number): boolean {
  if (stepFocusRequestedAt === null) return false;
  if (now - stepFocusRequestedAt > STEP_FOCUS_WINDOW_MS) {
    stepFocusRequestedAt = null;
    return false;
  }
  return true;
}

/**
 * Whether a user's press asked for the next step's heading (read only). For a
 * page outside the flow shell (account creation, a PageShell page) that takes
 * the request itself (rc.13, c6-3).
 */
export function stepFocusRequested(now: number = Date.now()): boolean {
  return stepFocusPending(now);
}

/** The request is honoured: forget it. */
export function clearStepFocusRequest(): void {
  stepFocusRequestedAt = null;
}

/*
 * ARRIVED BY BACK OR FORWARD (rc.13, c6-3). A history navigation is not a
 * user's step advance: a screen it mounts must not move focus (a guest screen
 * re-mounted by Forward used to focus its heading). A screen mounting within
 * a moment of a `popstate`, with no press asking for focus, arrived by
 * history.
 */
const HISTORY_ARRIVAL_MS = 1_000;
let lastHistoryNavigationAt = Number.NEGATIVE_INFINITY;
if (typeof window !== "undefined") {
  // Capture: on the window, a capture listener runs before the router's own
  // (bubble) one whatever the module order, so the instant is set before any
  // screen the navigation renders reads it (review of rc.13, P3).
  window.addEventListener(
    "popstate",
    () => {
      lastHistoryNavigationAt = performance.now();
    },
    { capture: true }
  );
}

/** This mount comes from Back or Forward, and no press asked for focus. */
export function arrivedByHistory(now: number = performance.now()): boolean {
  return now - lastHistoryNavigationAt < HISTORY_ARRIVAL_MS && !stepFocusPending(Date.now());
}

/** Test seam: forget a pending request and any history navigation. */
export function __resetStepFocusForTests(): void {
  stepFocusRequestedAt = null;
  lastHistoryNavigationAt = Number.NEGATIVE_INFINITY;
}

export function FlowShell({ children }: { children?: ReactNode }) {
  const shared = useContext(FlowShellContext);
  const screen = shared?.chrome?.testId;
  const element = shared?.element ?? null;
  useLayoutEffect(() => {
    if (!screen || !element) return;
    if (!stepFocusPending(Date.now())) return;
    // The step moved focus itself on arrival (Review's uncertain notice, a
    // rejected field): that is the more specific answer, so it stands (review
    // P2). Focus left in the shell's header (the lead's Back is reused across
    // steps) or on <body> is not the step's choice.
    const active = document.activeElement;
    if (active && active !== document.body && element.contains(active) && !active.closest(".b2-stephead")) {
      stepFocusRequestedAt = null;
      return;
    }
    // Only a focusable heading takes the request; a skeleton has none, so it
    // waits for the step behind it.
    const heading = element.querySelector<HTMLElement>("h1[tabindex]");
    if (!heading) return;
    stepFocusRequestedAt = null;
    heading.focus();
  }, [screen, element]);
  if (!shared) {
    // Not under a provider (a unit test, a screen rendered alone): its own.
    return (
      <FlowShellProvider>
        <FlowShell>{children}</FlowShell>
      </FlowShellProvider>
    );
  }
  return (
    <FrameMarkup chrome={shared.chrome} mainRef={shared.setElement}>
      {children}
    </FrameMarkup>
  );
}

/**
 * Render `children` into the mounted shell's <main> (the guest checkout's
 * screens, from GuestFlowHost). Under a provider with no shell mounted, it
 * renders NOTHING: never a second <main>. With no provider at all (a unit
 * test, the dev harness) it renders in place, where FlowFrame draws its own.
 */
export function FlowShellPortal({ children }: { children: ReactNode }) {
  const shared = useContext(FlowShellContext);
  if (!shared) return <>{children}</>;
  if (!shared.element) return null;
  return createPortal(children, shared.element);
}

/**
 * A flow frame: the step's <main> and header with `lead`, then `children`
 * (its scroll area and action bar). Inside FlowShell it publishes and draws
 * only `children`; alone it draws everything, as before c6.
 */
export function FlowFrame({ children, ...chrome }: FlowChrome & { children: ReactNode }) {
  const shared = useContext(FlowShellContext);
  const publish = shared?.publish ?? null;
  const { testId, className, data, lead, variant } = chrome;
  const dataKey = JSON.stringify(data ?? {});
  useLayoutEffect(() => {
    if (!publish) return;
    publish({ testId, className, data: JSON.parse(dataKey) as FlowChrome["data"], lead, variant });
  }, [publish, testId, className, dataKey, lead, variant]);
  useLayoutEffect(() => {
    if (!publish) return;
    return () => publish(null);
  }, [publish]);
  if (publish) return <>{children}</>;
  return <FrameMarkup chrome={chrome}>{children}</FrameMarkup>;
}
